/** Historical interventions and future obligations for a vessel or equipment record. */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { AssetReference } from "@/features/entities/AssetReference";
import { PersonReference } from "@/features/entities/PersonReference";
import { Attachments } from "@/features/documents/Attachments";
import {
  Details,
  EmptyState,
  ErrorState,
  LoadingState,
  StatusBadge,
} from "@/shared/components/page";
import { formatDateTime, formatMoney, formatQuantity } from "@/shared/lib/format";
import { collectPages, usePagedQuery } from "@/shared/lib/paging";
import { Button } from "@/shared/ui/button";

function Intervention({ job }: { job: Schemas["WorkOrderDetailOut"] }): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [expanded, setExpanded] = useState(false);
  return (
    <article className="space-y-3 rounded-md border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link
          to="/work-orders/$woId"
          params={{ woId: job.id }}
          className="font-medium text-primary underline"
        >
          {job.code} · {job.title}
        </Link>
        <StatusBadge value={job.status} namespace="workOrders.status" />
      </div>
      <Details
        items={[
          {
            label: t("workOrders.asset"),
            value: <AssetReference id={job.asset.id} name={job.asset.name} context="history" />,
          },
          { label: t("assets.history.technicalCompletion"), value: formatDateTime(job.actual_end) },
          {
            label: t("assets.history.acceptance"),
            value: job.accepted_at
              ? `${formatDateTime(job.accepted_at)} · ${job.accepted_by_name ?? t("assets.history.system")}`
              : "—",
          },
          { label: t("assets.history.release"), value: formatDateTime(job.released_at) },
          {
            label: t("assets.history.technicians"),
            value: job.assignees.length
              ? job.assignees.map((person, index) => (
                  <span key={person.membership_id}>
                    {index ? ", " : null}
                    <PersonReference id={person.membership_id} name={person.display_name} />
                  </span>
                ))
              : "—",
          },
          ...(job.actual_cost
            ? [{ label: t("assets.history.cost"), value: formatMoney(job.actual_cost) }]
            : []),
        ]}
      />
      <div className="flex flex-wrap gap-3 text-sm">
        {job.service_request_id ? (
          <Link
            to="/requests/$requestId"
            params={{ requestId: job.service_request_id }}
            className="text-primary underline"
          >
            {t("assets.history.sourceFault")}
          </Link>
        ) : null}
        {job.parent_id ? (
          <Link
            to="/work-orders/$woId"
            params={{ woId: job.parent_id }}
            className="text-primary underline"
          >
            {t("assets.history.previousIntervention")}
          </Link>
        ) : null}
        {job.vessel_id ? (
          <AssetReference
            id={job.vessel_id}
            name={t("assets.history.originalVessel")}
            context="history"
          />
        ) : null}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        {expanded ? t("assets.history.hideRecord") : t("assets.history.viewRecord")}
      </Button>
      {expanded ? (
        <div className="space-y-4">
          <p className="whitespace-pre-wrap text-sm">{job.completion_notes}</p>
          {job.failure_report ? (
            <p className="whitespace-pre-wrap text-sm">{job.failure_report.narrative}</p>
          ) : null}
          <section>
            <h3 className="font-medium">{t("assets.history.tasksAndTests")}</h3>
            {job.functional_test_result ? (
              <StatusBadge
                value={job.functional_test_result}
                namespace="assets.history.testResult"
              />
            ) : null}
            <p className="whitespace-pre-wrap text-sm">{job.functional_test_notes}</p>
            <ul className="space-y-2">
              {job.tasks.map((task) => (
                <li key={task.id} className="rounded border p-2 text-sm">
                  <p>
                    {task.title} ·{" "}
                    <StatusBadge value={task.status} namespace="workOrders.taskStatus" /> ·{" "}
                    {task.performer_name ??
                      t(task.completed_at ? "assets.history.system" : "assets.history.notRecorded")}
                  </p>
                  <p className="whitespace-pre-wrap">{task.notes}</p>
                  <ul className="ms-4 list-disc">
                    {task.checklist.map((check) => (
                      <li key={check.id}>
                        {check.prompt}:{" "}
                        {typeof check.value === "string"
                          ? check.value
                          : JSON.stringify(check.value)}{" "}
                        {check.note}{" "}
                        {check.passed !== null ? (
                          <StatusBadge
                            value={check.passed ? "passed" : "failed"}
                            namespace="assets.history.testResult"
                          />
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h3 className="font-medium">{t("assets.history.labourAndMaterials")}</h3>
            <ul className="space-y-1 text-sm">
              {job.labour.map((entry) => (
                <li key={entry.id}>
                  <PersonReference
                    id={entry.membership_id}
                    name={entry.performer_name ?? t("assets.history.notRecorded")}
                  />{" "}
                  · {formatQuantity(entry.hours)} {t("assets.history.hours")} · {entry.activity} ·{" "}
                  {formatDateTime(entry.started_at)}
                </li>
              ))}
              {job.parts.map((part) => (
                <li key={part.id}>
                  {part.item_sku} · {part.item_name} · {formatQuantity(part.quantity)} {part.uom} ·{" "}
                  <StatusBadge value={part.status} namespace="workOrders.partStatus" />
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h3 className="font-medium">{t("assets.statusHistory")}</h3>
            <ul className="space-y-1 text-sm">
              {job.status_history.map((change, index) => (
                <li key={`${change.occurred_at}-${index}`}>
                  {formatDateTime(change.occurred_at)} ·{" "}
                  <StatusBadge value={change.to_status} namespace="workOrders.status" /> ·{" "}
                  <PersonReference
                    id={change.actor_id}
                    name={change.actor_name ?? t("assets.history.system")}
                  />{" "}
                  · {change.reason}
                </li>
              ))}
              {job.downtime.map((period, index) => (
                <li key={`${period.started_at}-${index}`}>
                  {t("assets.history.downtime")}: {formatDateTime(period.started_at)} –{" "}
                  {formatDateTime(period.ended_at)} · {period.reason}
                </li>
              ))}
            </ul>
            {job.release_reason ? <p className="text-sm">{job.release_reason}</p> : null}
          </section>
          {can("documents.document.view") ? (
            <Attachments entityType="workorder" entityId={job.id} />
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

/** Paginated, permission-scoped closed and open history, including original vessel snapshots. */
export function AssetMaintenanceHistory({ assetId }: { assetId: string }): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const history = usePagedQuery(["assets", assetId, "history"], async (cursor) =>
    unwrap(
      await api.GET("/api/v1/fleet/assets/{asset_id}/history", {
        params: { path: { asset_id: assetId }, query: { cursor, limit: 20 } },
      }),
    ),
  );
  const future = useQuery({
    queryKey: ["schedules", "asset-history", assetId],
    queryFn: () =>
      collectPages(async (cursor) =>
        unwrap(
          await api.GET("/api/v1/maintenance/schedules", {
            params: {
              query: { asset_id: assetId, include_descendants: true, cursor, limit: 200 },
            },
          }),
        ),
      ),
    enabled: can("maintenance.plan.view"),
  });
  const operationalHistory = useQuery({
    queryKey: ["assets", assetId, "status-history"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/assets/{asset_id}/status-history", {
          params: { path: { asset_id: assetId } },
        }),
      ),
  });
  if (history.query.isPending) return <LoadingState />;
  if (history.query.isError)
    return <ErrorState error={history.query.error} onRetry={() => void history.query.refetch()} />;
  return (
    <div className="space-y-5">
      <section className="space-y-3">
        {history.items.length ? (
          history.items.map((job) => <Intervention key={job.id} job={job} />)
        ) : (
          <EmptyState message={t("assets.history.empty")} />
        )}
        {history.hasMore ? (
          <Button variant="outline" onClick={history.loadMore} disabled={history.isLoadingMore}>
            {t("app.loadMore")}
          </Button>
        ) : null}
      </section>
      <section className="space-y-2">
        <h2 className="font-medium">{t("assets.statusHistory")}</h2>
        {operationalHistory.isPending ? (
          <LoadingState />
        ) : operationalHistory.isError ? (
          <ErrorState
            error={operationalHistory.error}
            onRetry={() => void operationalHistory.refetch()}
          />
        ) : (
          <ul className="space-y-1 text-sm">
            {operationalHistory.data.map((change) => (
              <li key={change.id}>
                {formatDateTime(change.occurred_at)} ·{" "}
                <StatusBadge value={change.to_status} namespace="assets.status" /> ·{" "}
                <PersonReference
                  id={change.actor_id}
                  name={change.actor_name ?? t("assets.history.system")}
                />{" "}
                · {change.reason}
                {change.workorder_id ? (
                  <>
                    {" "}
                    ·{" "}
                    <Link
                      to="/work-orders/$woId"
                      params={{ woId: change.workorder_id }}
                      className="text-primary underline"
                    >
                      {t("assets.history.viewRecord")}
                    </Link>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
      {can("maintenance.plan.view") ? (
        <section className="space-y-2">
          <h2 className="font-medium">{t("assets.futureMaintenance")}</h2>
          {future.isPending ? (
            <LoadingState />
          ) : future.isError ? (
            <ErrorState error={future.error} onRetry={() => void future.refetch()} />
          ) : (
            <ul className="space-y-1 text-sm">
              {future.data.map((schedule) => (
                <li key={schedule.id}>
                  <Link
                    to="/maintenance"
                    search={{ q: schedule.asset_code }}
                    className="text-primary underline"
                  >
                    {schedule.plan_name}
                  </Link>
                  {" · "}
                  <StatusBadge value={schedule.status} namespace="maintenance.status" />
                  {" · "}
                  {schedule.next_due_at
                    ? formatDateTime(schedule.next_due_at)
                    : schedule.next_due_meter !== null
                      ? t("assets.history.meterDue", {
                          value: formatQuantity(schedule.next_due_meter),
                        })
                      : "—"}
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </div>
  );
}
